import React, { useState, useEffect, useCallback } from 'react';
import FitTable from '../components/FitTable';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface Teebox {
  course_teebox_id?: number;
  course_id?: number;
  course_teebox_name: string;
  course_teebox_yards: number;
  course_teebox_par: number;
  course_teebox_rating: number;
  course_teebox_slope: number;
  round_count?: number;
  [key: string]: any; // hole_N_par / hole_N_yards
}

interface Alias {
  alias_id: number;
  alias_name: string;
  normalized_name?: string;
}

interface Course {
  course_id?: number;
  course_name: string;
  facility_id?: number | null;
  address?: string | null;
  auto_created?: boolean;
  is_active?: boolean;
  teeboxes?: Teebox[];
  aliases?: Alias[];
}

interface Facility {
  facility_id: number | null;
  facility_name: string;
  address?: string | null;
  auto_created?: boolean;
  courses: Course[];
  aliases?: Alias[];
}

/** What's currently being merged, driving the merge modal. */
type MergeTarget =
  | { kind: 'facility'; source: Facility }
  | { kind: 'course'; source: Course; sourceFacility: Facility }
  | { kind: 'teebox'; source: Teebox; sourceCourse: Course };

/* ─── Component ──────────────────────────────────────────────────────────── */

const Courses: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedFac, setExpandedFac] = useState<Record<string, boolean>>({});
  const [expandedCourse, setExpandedCourse] = useState<Record<number, boolean>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [merge, setMerge] = useState<MergeTarget | null>(null);
  const [merging, setMerging] = useState(false);

  const auth = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const load = useCallback(async () => {
    try {
      const cfg = await auth();
      const res = await axios.get(`${API_BASE_URL}/facilities/with-courses`, cfg);
      setFacilities(res.data || []);
      setLoading(false);
    } catch (e) {
      console.error('Error loading facilities:', e);
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  const flashSaved = (id: string) => {
    setSavedId(id);
    setTimeout(() => setSavedId((cur) => (cur === id ? null : cur)), 1500);
  };

  const facKey = (f: Facility) => (f.facility_id == null ? 'unassigned' : `f${f.facility_id}`);

  /* ── Facility ops ──────────────────────────────────────────────────────── */
  const addFacility = () => {
    setQuery('');
    const tempId = -Date.now();
    setFacilities([{ facility_id: tempId, facility_name: '', address: '', auto_created: false, courses: [] }, ...facilities]);
    setExpandedFac((p) => ({ ...p, [`f${tempId}`]: true }));
  };

  const patchFacilityLocal = (key: string, patch: Partial<Facility>) =>
    setFacilities((prev) => prev.map((f) => (facKey(f) === key ? { ...f, ...patch } : f)));

  const saveFacility = async (f: Facility) => {
    const key = facKey(f);
    setSavingId(key);
    try {
      const cfg = await auth();
      if (!f.facility_id || f.facility_id < 0) {
        const res = await axios.post(`${API_BASE_URL}/facilities`, { facility_name: f.facility_name, address: f.address }, cfg);
        patchFacilityLocal(key, { facility_id: res.data.facility_id });
      } else {
        await axios.put(`${API_BASE_URL}/facilities/${f.facility_id}`, { facility_name: f.facility_name, address: f.address }, cfg);
      }
      await load();
      flashSaved(key);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to save facility.');
    } finally { setSavingId(null); }
  };

  const verifyFacility = async (f: Facility) => {
    if (!f.facility_id) return;
    const cfg = await auth();
    await axios.patch(`${API_BASE_URL}/facilities/${f.facility_id}/verify`, {}, cfg);
    await load();
  };

  const verifyCourse = async (c: Course) => {
    if (!c.course_id || c.course_id < 0) return;
    const cfg = await auth();
    await axios.patch(`${API_BASE_URL}/courses/${c.course_id}/verify`, {}, cfg);
    await load();
  };

  const deleteFacility = async (f: Facility) => {
    if (!f.facility_id) { setFacilities((prev) => prev.filter((x) => facKey(x) !== facKey(f))); return; }
    if (!window.confirm(`Delete facility "${f.facility_name}"? Its courses will become unassigned.`)) return;
    const cfg = await auth();
    await axios.delete(`${API_BASE_URL}/facilities/${f.facility_id}`, cfg);
    await load();
  };

  const removeFacilityAlias = async (a: Alias) => {
    if (!window.confirm(`Remove alias "${a.alias_name}"? New reservations using that name will no longer auto-match this facility.`)) return;
    const cfg = await auth();
    await axios.delete(`${API_BASE_URL}/facilities/aliases/${a.alias_id}`, cfg);
    await load();
  };

  const removeCourseAlias = async (a: Alias) => {
    if (!window.confirm(`Remove alias "${a.alias_name}"? New reservations using that name will no longer auto-match this course.`)) return;
    const cfg = await auth();
    await axios.delete(`${API_BASE_URL}/courses/aliases/${a.alias_id}`, cfg);
    await load();
  };

  /* ── Merge (facilities + courses) ──────────────────────────────────────── */
  const runMerge = async (targetId: number) => {
    if (!merge) return;
    setMerging(true);
    try {
      const cfg = await auth();
      if (merge.kind === 'facility') {
        await axios.post(`${API_BASE_URL}/facilities/${merge.source.facility_id}/merge`, { target_facility_id: targetId }, cfg);
      } else if (merge.kind === 'course') {
        await axios.post(`${API_BASE_URL}/courses/${merge.source.course_id}/merge`, { target_course_id: targetId }, cfg);
      } else {
        await axios.post(`${API_BASE_URL}/teeboxes/${merge.source.course_teebox_id}/merge`, { target_course_teebox_id: targetId }, cfg);
      }
      setMerge(null);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Merge failed.');
    } finally { setMerging(false); }
  };

  /* ── Course ops ────────────────────────────────────────────────────────── */
  const addCourse = (f: Facility) => {
    setQuery('');
    const tempCourse: Course = { course_id: -Date.now(), course_name: '', facility_id: f.facility_id, teeboxes: [] };
    patchFacilityLocal(facKey(f), { courses: [tempCourse, ...f.courses] });
    if (f.facility_id != null) setExpandedFac((p) => ({ ...p, [facKey(f)]: true }));
  };

  const patchCourseLocal = (facK: string, courseId: number, patch: Partial<Course>) =>
    setFacilities((prev) => prev.map((f) =>
      facKey(f) === facK ? { ...f, courses: f.courses.map((c) => (c.course_id === courseId ? { ...c, ...patch } : c)) } : f
    ));

  const saveCourse = async (f: Facility, c: Course) => {
    const key = `c${c.course_id}`;
    setSavingId(key);
    try {
      const cfg = await auth();
      if (!c.course_id || c.course_id < 0) {
        await axios.post(`${API_BASE_URL}/courses`, { course_name: c.course_name, facility_id: f.facility_id, address: c.address }, cfg);
      } else {
        await axios.put(
          `${API_BASE_URL}/courses/${c.course_id}`,
          { course_name: c.course_name, facility_id: f.facility_id, address: c.address, is_active: c.is_active !== false },
          cfg,
        );
      }
      await load();
      flashSaved(key);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to save course.');
    } finally { setSavingId(null); }
  };

  const toggleCourseActive = async (f: Facility, c: Course, nextActive: boolean) => {
    if (!c.course_id || c.course_id < 0) {
      patchCourseLocal(facKey(f), c.course_id as number, { is_active: nextActive });
      return;
    }
    if (!nextActive && !window.confirm(
      `Mark "${c.course_name}" inactive?\n\nIt will be hidden from course pickers (new reservations, rounds, tournament config), ` +
      `but existing data that already references it will keep showing the course name.`,
    )) return;
    patchCourseLocal(facKey(f), c.course_id as number, { is_active: nextActive });
    try {
      const cfg = await auth();
      await axios.put(`${API_BASE_URL}/courses/${c.course_id}`, { is_active: nextActive }, cfg);
      await load();
    } catch (e: any) {
      patchCourseLocal(facKey(f), c.course_id as number, { is_active: !nextActive });
      alert(e?.response?.data?.error || 'Failed to update course status.');
    }
  };

  const deleteCourse = async (c: Course) => {
    if (!c.course_id || c.course_id < 0) { await load(); return; }
    if (!window.confirm(`Delete course "${c.course_name}"? This removes its teeboxes.`)) return;
    const cfg = await auth();
    await axios.delete(`${API_BASE_URL}/courses/${c.course_id}`, cfg);
    await load();
  };

  /* ── Teebox ops ────────────────────────────────────────────────────────── */
  const patchTeeboxLocal = (courseId: number, teeIdx: number, patch: Partial<Teebox>) =>
    setFacilities((prev) => prev.map((f) => ({
      ...f,
      courses: f.courses.map((c) => c.course_id === courseId
        ? { ...c, teeboxes: (c.teeboxes || []).map((t, i) => (i === teeIdx ? { ...t, ...patch } : t)) }
        : c),
    })));

  const addTeebox = async (c: Course) => {
    if (!c.course_id || c.course_id < 0) { alert('Save the course first.'); return; }
    const cfg = await auth();
    await axios.post(`${API_BASE_URL}/teeboxes`, {
      course_id: c.course_id, course_teebox_name: 'New Teebox',
      course_teebox_yards: 0, course_teebox_par: 0, course_teebox_rating: 0, course_teebox_slope: 0,
    }, cfg);
    await load();
    setExpandedCourse((p) => ({ ...p, [c.course_id as number]: true }));
  };

  const saveTeebox = async (t: Teebox) => {
    const key = `t${t.course_teebox_id}`;
    setSavingId(key);
    try {
      const cfg = await auth();
      await axios.put(`${API_BASE_URL}/teeboxes/${t.course_teebox_id}`, t, cfg);
      flashSaved(key);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to save teebox.');
    } finally { setSavingId(null); }
  };

  const deleteTeebox = async (t: Teebox) => {
    if (!window.confirm(`Delete teebox "${t.course_teebox_name}"?`)) return;
    try {
      const cfg = await auth();
      await axios.delete(`${API_BASE_URL}/teeboxes/${t.course_teebox_id}`, cfg);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to delete tee box.');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="cx-page">
        <style>{cx}</style>
        <div className="cx-inner">
          <div className="cx-gate">
            <h1 className="cx-h1">Courses</h1>
            <p className="cx-sub">You must be logged in to manage facilities and courses.</p>
            <button className="cx-btn cx-btn-primary" onClick={() => loginWithRedirect()}>Log in</button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Derived: search filtering ─────────────────────────────────────────── */
  const q = query.trim().toLowerCase();
  const facMatches = (f: Facility) =>
    !q || f.facility_name.toLowerCase().includes(q) || f.courses.some((c) => c.course_name.toLowerCase().includes(q));
  const visibleCourses = (f: Facility) => {
    if (!q || f.facility_name.toLowerCase().includes(q)) return f.courses;
    return f.courses.filter((c) => c.course_name.toLowerCase().includes(q));
  };
  const visibleFacilities = facilities.filter(facMatches);

  const totalCourses = facilities.reduce((a, f) => a + f.courses.length, 0);
  const needsReview =
    facilities.filter((f) => f.auto_created).length +
    facilities.reduce((a, f) => a + f.courses.filter((c) => c.auto_created).length, 0);

  return (
    <div className="cx-page">
      <style>{cx}</style>
      <div className="cx-inner">
        {/* Header */}
        <div className="cx-header">
          <div>
            <h1 className="cx-h1">Courses &amp; Facilities</h1>
            <p className="cx-sub">Facilities group courses; each course holds its tee boxes and per-hole detail.</p>
          </div>
          <button className="cx-btn cx-btn-primary" onClick={addFacility}>+ Add facility</button>
        </div>

        {/* Toolbar */}
        <div className="cx-toolbar">
          <div className="cx-search">
            <span className="cx-search-ico">🔍</span>
            <input
              className="cx-search-input"
              placeholder="Search facilities or courses…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && <button className="cx-search-clear" onClick={() => setQuery('')} aria-label="Clear">✕</button>}
          </div>
          <div className="cx-stats">
            <span className="cx-stat"><b>{facilities.length}</b> facilities</span>
            <span className="cx-stat-dot">·</span>
            <span className="cx-stat"><b>{totalCourses}</b> courses</span>
            {needsReview > 0 && (
              <>
                <span className="cx-stat-dot">·</span>
                <span className="cx-stat cx-stat-warn"><b>{needsReview}</b> need review</span>
              </>
            )}
          </div>
        </div>

        {loading ? (
          <div className="cx-loading">Loading…</div>
        ) : visibleFacilities.length === 0 ? (
          <div className="cx-empty">
            {q ? `No facilities or courses match “${query}”.` : 'No facilities yet. Add one to get started.'}
          </div>
        ) : (
          visibleFacilities.map((f) => {
            const key = facKey(f);
            const open = expandedFac[key] ?? !!q;
            const isUnassigned = f.facility_id == null;
            const persisted = !!f.facility_id && f.facility_id > 0;
            const courses = visibleCourses(f);
            return (
              <div key={key} className="cx-fac">
                <div className="cx-fac-head">
                  <div className="cx-fac-id">
                    {isUnassigned ? (
                      <div className="cx-fac-title-static">{f.facility_name || 'Unassigned courses'}</div>
                    ) : (
                      <input
                        className="cx-title-input"
                        placeholder="Facility name"
                        value={f.facility_name}
                        onChange={(e) => patchFacilityLocal(key, { facility_name: e.target.value })}
                      />
                    )}
                    {!isUnassigned && (
                      <input
                        className="cx-addr-input"
                        placeholder="Address (optional)"
                        value={f.address || ''}
                        onChange={(e) => patchFacilityLocal(key, { address: e.target.value })}
                      />
                    )}
                  </div>
                  <div className="cx-fac-meta">
                    <span className="cx-badge cx-badge-blue">{f.courses.length} course{f.courses.length !== 1 ? 's' : ''}</span>
                    {f.auto_created && <span className="cx-badge cx-badge-amber">Needs review</span>}
                  </div>
                </div>

                {!isUnassigned && (f.aliases?.length ?? 0) > 0 && (
                  <div className="cx-aliases">
                    <span className="cx-aliases-label">Also known as</span>
                    {f.aliases!.map((a) => (
                      <span key={a.alias_id} className="cx-alias">
                        {a.alias_name}
                        <button className="cx-alias-x" title="Remove alias" onClick={() => removeFacilityAlias(a)}>✕</button>
                      </span>
                    ))}
                  </div>
                )}

                {!isUnassigned && (
                  <div className="cx-actions">
                    <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={savingId === key} onClick={() => saveFacility(f)}>
                      {savingId === key ? 'Saving…' : savedId === key ? 'Saved ✓' : persisted ? 'Save' : 'Create'}
                    </button>
                    {persisted && (
                      <>
                        <button className="cx-btn cx-btn-secondary cx-btn-sm" onClick={() => addCourse(f)}>+ Course</button>
                        {f.auto_created && <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => verifyFacility(f)}>Mark reviewed</button>}
                        <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setMerge({ kind: 'facility', source: f })}>Merge…</button>
                        <button className="cx-btn cx-btn-danger cx-btn-sm cx-push" onClick={() => deleteFacility(f)}>Delete</button>
                      </>
                    )}
                  </div>
                )}
                {isUnassigned && (
                  <div className="cx-actions"><div className="cx-note">Assign these courses to a facility by editing them, or merge facilities above.</div></div>
                )}

                <button className="cx-expand" onClick={() => setExpandedFac((p) => ({ ...p, [key]: !open }))} aria-expanded={open}>
                  <span className={`cx-chev ${open ? 'open' : ''}`}>▸</span>
                  {open ? 'Hide courses' : `Show ${f.courses.length} course${f.courses.length !== 1 ? 's' : ''}`}
                </button>

                {open && (
                  <div className="cx-fac-body">
                    <div className="cx-courses">
                      {courses.length === 0 && <div className="cx-empty-sm">No courses yet — use “+ Course” above to add one.</div>}
                      {courses.map((c) => {
                        const cOpen = c.course_id ? (expandedCourse[c.course_id] ?? false) : true;
                        const cKey = `c${c.course_id}`;
                        const isActive = c.is_active !== false;
                        const isPersisted = !!c.course_id && c.course_id > 0;
                        return (
                          <div key={c.course_id} className={`cx-course ${isActive ? '' : 'inactive'}`}>
                            <div className="cx-course-head">
                              <input
                                className="cx-title-input cx-title-sm"
                                placeholder="Course name"
                                value={c.course_name}
                                onChange={(e) => patchCourseLocal(key, c.course_id as number, { course_name: e.target.value })}
                              />
                              <div className="cx-course-tags">
                                {c.auto_created && <span className="cx-badge cx-badge-amber">Review</span>}
                                {isPersisted && (
                                  <span className={`cx-badge ${isActive ? 'cx-badge-green' : 'cx-badge-gray'}`}>
                                    {isActive ? 'Active' : 'Inactive'}
                                  </span>
                                )}
                              </div>
                              <div className="cx-course-actions">
                                {c.auto_created && isPersisted && (
                                  <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => verifyCourse(c)}>Reviewed</button>
                                )}
                                {isPersisted && (
                                  <button
                                    className="cx-btn cx-btn-ghost cx-btn-sm"
                                    title={isActive ? 'Hide from new assignments (existing data unaffected).' : 'Re-enable for new assignments.'}
                                    onClick={() => toggleCourseActive(f, c, !isActive)}
                                  >
                                    {isActive ? 'Deactivate' : 'Activate'}
                                  </button>
                                )}
                                <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={savingId === cKey} onClick={() => saveCourse(f, c)}>
                                  {savingId === cKey ? 'Saving…' : savedId === cKey ? 'Saved ✓' : isPersisted ? 'Save' : 'Create'}
                                </button>
                                {isPersisted && (
                                  <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setMerge({ kind: 'course', source: c, sourceFacility: f })}>Merge…</button>
                                )}
                                <button className="cx-btn cx-btn-danger cx-btn-sm" onClick={() => deleteCourse(c)}>Delete</button>
                              </div>
                            </div>

                            {(c.aliases?.length ?? 0) > 0 && (
                              <div className="cx-aliases cx-aliases-course">
                                <span className="cx-aliases-label">Also known as</span>
                                {c.aliases!.map((a) => (
                                  <span key={a.alias_id} className="cx-alias">
                                    {a.alias_name}
                                    <button className="cx-alias-x" title="Remove alias" onClick={() => removeCourseAlias(a)}>✕</button>
                                  </span>
                                ))}
                              </div>
                            )}

                            {isPersisted && (
                              <button
                                className="cx-expand cx-expand-sub"
                                onClick={() => setExpandedCourse((p) => ({ ...p, [c.course_id as number]: !cOpen }))}
                                aria-expanded={cOpen}
                              >
                                <span className={`cx-chev ${cOpen ? 'open' : ''}`}>▸</span>
                                {cOpen ? 'Hide tee boxes' : `Tee boxes (${(c.teeboxes || []).length})`}
                              </button>
                            )}

                            {isPersisted && cOpen && (
                              <div className="cx-tees">
                                <div className="cx-tees-head">
                                  <span className="cx-tees-title">Tee boxes</span>
                                  <button className="cx-btn cx-btn-secondary cx-btn-sm" onClick={() => addTeebox(c)}>+ Tee box</button>
                                </div>
                                <TeeboxAgentPanel courseId={c.course_id as number} auth={auth} onChanged={load} />
                                {(c.teeboxes || []).length === 0 && <div className="cx-empty-sm">No tee boxes yet.</div>}
                                {(c.teeboxes || []).map((t, ti) => (
                                  <TeeboxEditor
                                    key={t.course_teebox_id ?? ti}
                                    teebox={t}
                                    canMerge={(c.teeboxes || []).filter((x) => x.course_teebox_id).length > 1}
                                    saving={savingId === `t${t.course_teebox_id}`}
                                    saved={savedId === `t${t.course_teebox_id}`}
                                    onChange={(patch) => patchTeeboxLocal(c.course_id as number, ti, patch)}
                                    onSave={() => saveTeebox(t)}
                                    onMerge={() => setMerge({ kind: 'teebox', source: t, sourceCourse: c })}
                                    onDelete={() => deleteTeebox(t)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {merge && (
        <MergeModal
          merge={merge}
          facilities={facilities}
          merging={merging}
          onCancel={() => setMerge(null)}
          onConfirm={runMerge}
        />
      )}
    </div>
  );
};

/* ─── Merge modal (facilities + courses) ──────────────────────────────────── */

const MergeModal: React.FC<{
  merge: MergeTarget;
  facilities: Facility[];
  merging: boolean;
  onCancel: () => void;
  onConfirm: (targetId: number) => void;
}> = ({ merge, facilities, merging, onCancel, onConfirm }) => {
  const [targetId, setTargetId] = useState<number | ''>('');
  const [confirmed, setConfirmed] = useState(false);

  const isFacility = merge.kind === 'facility';
  const isCourse = merge.kind === 'course';
  const isTeebox = merge.kind === 'teebox';
  const noun = isFacility ? 'facility' : isCourse ? 'course' : 'tee box';
  const nounPlural = isFacility ? 'facilities' : isCourse ? 'courses' : 'tee boxes';
  const sourceName =
    merge.kind === 'facility' ? merge.source.facility_name
    : merge.kind === 'course' ? merge.source.course_name
    : merge.source.course_teebox_name;

  // Candidate targets — computed per kind so property access is type-safe.
  const facilityTargets =
    merge.kind === 'facility'
      ? facilities
          .filter((f) => f.facility_id != null && f.facility_id !== merge.source.facility_id)
          .map((f) => ({ id: f.facility_id as number, label: f.facility_name }))
      : [];

  // Flat list of all courses (excluding the source), labelled with their
  // facility. Same-facility courses are surfaced first since they're the
  // likely merge targets.
  const courseTargets =
    merge.kind === 'course'
      ? facilities
          .flatMap((f) =>
            f.courses
              .filter((c) => c.course_id && c.course_id > 0 && c.course_id !== merge.source.course_id)
              .map((c) => ({
                id: c.course_id as number,
                label: f.facility_id == null ? c.course_name : `${c.course_name} — ${f.facility_name}`,
                sameFacility: f.facility_id === merge.source.facility_id,
              })),
          )
          .sort((a, b) => (a.sameFacility === b.sameFacility ? a.label.localeCompare(b.label) : a.sameFacility ? -1 : 1))
      : [];

  // Sibling tee boxes on the same course (the only valid merge targets).
  const teeboxTargets =
    merge.kind === 'teebox'
      ? (merge.sourceCourse.teeboxes || [])
          .filter((t) => t.course_teebox_id && t.course_teebox_id !== merge.source.course_teebox_id)
          .map((t) => ({
            id: t.course_teebox_id as number,
            label: `${t.course_teebox_name}${t.course_teebox_rating ? ` · ${t.course_teebox_rating}/${t.course_teebox_slope}` : ''}`,
          }))
      : [];

  const targets = isFacility ? facilityTargets : isCourse ? courseTargets : teeboxTargets;
  const targetName = targets.find((t) => t.id === targetId)?.label;

  const canMerge = targetId !== '' && confirmed && !merging;

  // For tee-box merges, compare the source vs the chosen target field-by-field
  // so the admin sees exactly what matches, what differs, and what's missing
  // on the survivor (whose values become canonical for the moved rounds).
  const teeboxCmp = (() => {
    if (merge.kind !== 'teebox' || targetId === '') return null;
    const source = merge.source;
    const target = (merge.sourceCourse.teeboxes || []).find((t) => t.course_teebox_id === targetId);
    if (!target) return null;
    const fields: Array<{ key: string; label: string }> = [
      { key: 'course_teebox_par', label: 'Course par' },
      { key: 'course_teebox_rating', label: 'Course rating' },
      { key: 'course_teebox_slope', label: 'Slope rating' },
      { key: 'course_teebox_yards', label: 'Yardage' },
    ];
    const rows = fields.map((f) => {
      const s = Number(source[f.key]) || 0;
      const t = Number(target[f.key]) || 0;
      let status: 'match' | 'differ' | 'target-missing' | 'source-missing' | 'both-missing';
      if (!s && !t) status = 'both-missing';
      else if (!t) status = 'target-missing';
      else if (!s) status = 'source-missing';
      else if (s === t) status = 'match';
      else status = 'differ';
      return { ...f, s, t, status };
    });
    return {
      rows,
      sourceRounds: source.round_count || 0,
      targetRounds: target.round_count || 0,
      nameDiffers:
        (source.course_teebox_name || '').trim().toLowerCase() !==
        (target.course_teebox_name || '').trim().toLowerCase(),
      sourceTeeName: source.course_teebox_name,
      targetTeeName: target.course_teebox_name,
    };
  })();

  const fmt = (v: number) => (v ? String(v) : '—');
  const plural = (n: number) => (n === 1 ? '' : 's');

  return (
    <div className="cx-modal-overlay" onClick={onCancel}>
      <div className="cx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cx-modal-head">
          <h2 className="cx-modal-title">Merge {noun}</h2>
          <button className="cx-modal-x" onClick={onCancel} aria-label="Close">✕</button>
        </div>

        <div className="cx-modal-body">
          <p className="cx-modal-lead">
            Merge <b>{sourceName || '(unnamed)'}</b> into another {noun}. The selected {noun} survives;{' '}
            <b>{sourceName || 'this one'}</b> is removed.
          </p>

          <label className="cx-modal-label">Merge into</label>
          <select
            className="cx-modal-select"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Select a {noun}…</option>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          {targets.length === 0 && (
            <div className="cx-modal-note">No other {nounPlural} available to merge into.</div>
          )}

          <div className="cx-modal-what">
            <div className="cx-modal-what-title">What happens</div>
            <ul>
              {isFacility && (
                <>
                  <li>All of <b>{sourceName}</b>’s courses and reservations move to {targetName ? <b>{targetName}</b> : 'the target'}.</li>
                  <li><b>{sourceName}</b>’s name (and any of its aliases) is kept as an alias so future reservations still match.</li>
                </>
              )}
              {isCourse && (
                <>
                  <li><b>{sourceName}</b>’s tee boxes are moved onto {targetName ? <b>{targetName}</b> : 'the target course'}.</li>
                  <li>Every reference — rounds, reservations, scheduled rounds — is re-pointed to {targetName ? <b>{targetName}</b> : 'the target course'}.</li>
                  <li><b>{sourceName}</b>’s name (and any of its aliases) is kept as an alias so future reservations still match.</li>
                </>
              )}
              {isTeebox && (
                <>
                  <li>Every round that used <b>{sourceName}</b> is re-pointed to {targetName ? <b>{targetName}</b> : 'the target tee box'}.</li>
                  <li>Standings, points and stored net scores are <b>unaffected</b>.</li>
                  <li>Handicap indexes are computed from the tee box’s rating/slope, so the <b>survivor’s</b> rating/slope becomes canonical for those rounds. If the two tee boxes share the same rating/slope, indexes don’t change.</li>
                </>
              )}
            </ul>
          </div>

          {teeboxCmp && (
            <div className="cx-cmp">
              <div className="cx-cmp-rounds">
                Moving <b>{teeboxCmp.sourceRounds}</b> round{plural(teeboxCmp.sourceRounds)} from{' '}
                <b>{teeboxCmp.sourceTeeName}</b> onto <b>{teeboxCmp.targetTeeName}</b>, which already has{' '}
                <b>{teeboxCmp.targetRounds}</b> round{plural(teeboxCmp.targetRounds)} — the survivor will hold{' '}
                <b>{teeboxCmp.sourceRounds + teeboxCmp.targetRounds}</b>.
              </div>

              <FitTable>
              <table className="tbl cx-cmp-table">
                <thead>
                  <tr>
                    <th>Attribute</th>
                    <th>{teeboxCmp.sourceTeeName}</th>
                    <th>{teeboxCmp.targetTeeName}</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {teeboxCmp.rows.map((r) => (
                    <tr key={r.key} className={`cx-cmp-${r.status}`}>
                      <td>{r.label}</td>
                      <td>{fmt(r.s)}</td>
                      <td>{fmt(r.t)}</td>
                      <td>
                        {r.status === 'match' && '✓ Same'}
                        {r.status === 'differ' && '✗ Differs'}
                        {r.status === 'target-missing' && '⚠ Missing on survivor'}
                        {r.status === 'source-missing' && 'Only on survivor'}
                        {r.status === 'both-missing' && 'Not set'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </FitTable>

              {(() => {
                const issues: React.ReactNode[] = [];
                if (teeboxCmp.nameDiffers) {
                  issues.push(
                    <li key="name">
                      The tee box names are different (<b>{teeboxCmp.sourceTeeName}</b> vs{' '}
                      <b>{teeboxCmp.targetTeeName}</b>) — make sure these are really the same tee.
                    </li>,
                  );
                }
                for (const r of teeboxCmp.rows) {
                  if (r.status === 'differ') {
                    issues.push(
                      <li key={r.key}>
                        <b>{r.label}</b> differs — {teeboxCmp.sourceTeeName} has <b>{fmt(r.s)}</b>,{' '}
                        {teeboxCmp.targetTeeName} has <b>{fmt(r.t)}</b>. The survivor’s <b>{fmt(r.t)}</b> will apply to all
                        moved rounds.
                      </li>,
                    );
                  } else if (r.status === 'target-missing') {
                    issues.push(
                      <li key={r.key}>
                        The survivor <b>{teeboxCmp.targetTeeName}</b> has no <b>{r.label.toLowerCase()}</b> set — moved
                        rounds will use a blank/zero value
                        {r.key === 'course_teebox_slope'
                          ? ' (a 0 slope breaks handicap differentials).'
                          : r.key === 'course_teebox_rating'
                          ? ' (handicap differentials will be off).'
                          : '.'}
                      </li>,
                    );
                  }
                }
                return issues.length > 0 ? (
                  <div className="cx-modal-warn">
                    <div className="cx-cmp-warn-title">Heads up</div>
                    <ul className="cx-cmp-warn-list">{issues}</ul>
                  </div>
                ) : (
                  <div className="cx-cmp-ok">✓ Course par, course rating, slope rating and yardage all match — handicaps won’t change.</div>
                );
              })()}
            </div>
          )}

          <div className="cx-modal-warn">
            ⚠️ This is <b>irreversible</b>. <b>{sourceName}</b> will be permanently deleted and cannot be restored.
          </div>

          <label className="cx-modal-check">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span>I understand this merge is permanent and cannot be undone.</span>
          </label>
        </div>

        <div className="cx-modal-actions">
          <button className="cx-btn cx-btn-secondary" onClick={onCancel} disabled={merging}>Cancel</button>
          <button className="cx-btn cx-btn-danger-solid" disabled={!canMerge} onClick={() => onConfirm(targetId as number)}>
            {merging ? 'Merging…' : 'Merge — permanent'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Teebox editor ───────────────────────────────────────────────────────── */

const TeeboxEditor: React.FC<{
  teebox: Teebox;
  canMerge: boolean;
  saving: boolean;
  saved: boolean;
  onChange: (patch: Partial<Teebox>) => void;
  onSave: () => void;
  onMerge: () => void;
  onDelete: () => void;
}> = ({ teebox, canMerge, saving, saved, onChange, onSave, onMerge, onDelete }) => {
  const [showHoles, setShowHoles] = useState(false);

  const parFor = (start: number) => {
    let sum = 0;
    for (let i = start; i < start + 9; i++) sum += Number(teebox[`hole_${i + 1}_par`]) || 0;
    return sum;
  };
  const front = parFor(0);
  const back = parFor(9);
  const holesPar = front + back;

  const renderNine = (label: string, start: number, ninePar: number) => (
    <div className="cx-nine">
      <div className="cx-nine-head">
        <span>{label}</span>
        {ninePar > 0 && <span className="cx-nine-par">Par {ninePar}</span>}
      </div>
      <div className="cx-holes">
        {[...Array(9)].map((_, i) => {
          const hole = start + i;
          const parKey = `hole_${hole + 1}_par`;
          const ydKey = `hole_${hole + 1}_yards`;
          return (
            <div key={hole} className="cx-hole">
              <div className="cx-hole-n">{hole + 1}</div>
              <select className="cx-hole-par" value={teebox[parKey] || ''} onChange={(e) => onChange({ [parKey]: Number(e.target.value) })}>
                <option value="" disabled>Par</option>
                <option value={3}>3</option><option value={4}>4</option><option value={5}>5</option>
              </select>
              <input className="cx-hole-yds" type="number" min={1} placeholder="yds" value={teebox[ydKey] || ''}
                onChange={(e) => onChange({ [ydKey]: Number(e.target.value) })} />
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="cx-tee">
      <div className="cx-tee-fields">
        <div className="cx-field cx-field-wide">
          <label className="cx-label">Tee box</label>
          <input className="cx-input" value={teebox.course_teebox_name}
            onChange={(e) => onChange({ course_teebox_name: e.target.value })} />
        </div>
        <div className="cx-field">
          <label className="cx-label">Yards</label>
          <input className="cx-input" type="number" value={teebox.course_teebox_yards ?? 0}
            onChange={(e) => onChange({ course_teebox_yards: Number(e.target.value) })} />
        </div>
        <div className="cx-field">
          <label className="cx-label">Par</label>
          <input className="cx-input" type="number" value={teebox.course_teebox_par ?? 0}
            onChange={(e) => onChange({ course_teebox_par: Number(e.target.value) })} />
        </div>
        <div className="cx-field">
          <label className="cx-label">Rating</label>
          <input className="cx-input" type="number" step="0.1" value={teebox.course_teebox_rating ?? 0}
            onChange={(e) => onChange({ course_teebox_rating: Number(e.target.value) })} />
        </div>
        <div className="cx-field">
          <label className="cx-label">Slope</label>
          <input className="cx-input" type="number" value={teebox.course_teebox_slope ?? 0}
            onChange={(e) => onChange({ course_teebox_slope: Number(e.target.value) })} />
        </div>
      </div>

      <button className={`cx-holes-toggle ${showHoles ? 'open' : ''}`} onClick={() => setShowHoles((v) => !v)}>
        <span className="cx-chev">▸</span>
        Hole-by-hole
        {holesPar > 0 && <span className="cx-muted"> · Par {holesPar}</span>}
      </button>

      {showHoles && (
        <div className="cx-nines">
          {renderNine('Front 9', 0, front)}
          {renderNine('Back 9', 9, back)}
        </div>
      )}

      <div className="cx-tee-actions">
        <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={saving} onClick={onSave}>
          {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save tee box'}
        </button>
        {canMerge && teebox.course_teebox_id && (
          <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={onMerge}>Merge…</button>
        )}
        <button className="cx-btn cx-btn-danger cx-btn-sm" onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
};

/* ─── Tee-box research agent panel ───────────────────────────────────────────
   Per-course: trigger the AI agent, poll it, and review the resulting "set"
   (new tee boxes + proposed updates to existing ones) before confirming or
   declining. Runs against new AND existing courses — existing tee boxes are
   matched by name and updated (not overwritten) only once the set is confirmed. */

interface AgentChange { field: string; from: any; to: any }
interface ProposedUpdate { update_id: number; status: string; target_teebox_id: number; target_name: string; changes: AgentChange[] }
interface AgentRun {
  run: { run_id: number; status: string; trigger: string; summary?: string | null; error?: string | null; sources?: string[] | null };
  newTeeboxes: Teebox[];
  proposedUpdates: ProposedUpdate[];
}
interface DeclineKept { course_teebox_id: number; course_teebox_name: string; message: string; rounds: any[] }

const FIELD_LABELS: Record<string, string> = {
  course_teebox_name: 'Name', course_teebox_rating: 'Rating',
  course_teebox_slope: 'Slope', course_teebox_par: 'Par', course_teebox_yards: 'Yards',
};
const fieldLabel = (k: string): string => {
  if (FIELD_LABELS[k]) return FIELD_LABELS[k];
  const m = k.match(/^hole_(\d+)_(par|yards)$/);
  if (m) return `Hole ${m[1]} ${m[2]}`;
  return k;
};
const fmtVal = (v: any): string => (v == null || v === '' ? '—' : String(v));

const TeeboxAgentPanel: React.FC<{
  courseId: number;
  auth: () => Promise<{ headers: { Authorization: string } }>;
  onChanged: () => Promise<void> | void;
}> = ({ courseId, auth, onChanged }) => {
  const [run, setRun] = useState<AgentRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decline, setDecline] = useState<{ deleted: any[]; kept: DeclineKept[] } | null>(null);
  const [selNew, setSelNew] = useState<Set<number>>(new Set());
  const [selUpd, setSelUpd] = useState<Set<number>>(new Set());
  const [expandedTee, setExpandedTee] = useState<Set<number>>(new Set());
  const alive = React.useRef(true);
  const fileRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => () => { alive.current = false; }, []);

  const loadRun = useCallback(async (runId: number): Promise<AgentRun | null> => {
    const cfg = await auth();
    const res = await axios.get(`${API_BASE_URL}/teebox-agent-runs/${runId}`, cfg);
    return res.data as AgentRun;
  }, [auth]);

  const pollToken = React.useRef(0);
  // Poll a running run until it settles (bumping pollToken cancels the poll).
  const poll = useCallback(async (runId: number) => {
    const token = ++pollToken.current;
    for (let i = 0; i < 110; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      if (!alive.current || pollToken.current !== token) return;
      let data: AgentRun | null = null;
      try { data = await loadRun(runId); } catch { /* keep polling */ }
      if (!alive.current || pollToken.current !== token) return;
      if (data && data.run.status !== 'running') {
        // Keep the panel open only for states with something to review.
        setRun(['pending_review', 'failed'].includes(data.run.status) ? data : null);
        return;
      }
    }
    if (alive.current && pollToken.current === token) setError('The agent is taking longer than expected — check back shortly.');
  }, [loadRun]);

  // On mount / course change, surface any existing running or pending set.
  const refresh = useCallback(async () => {
    try {
      const cfg = await auth();
      const res = await axios.get(`${API_BASE_URL}/courses/${courseId}/teebox-agent-runs`, cfg);
      const latest = (res.data || [])[0];
      if (latest && (latest.status === 'running' || latest.status === 'pending_review')) {
        const data = await loadRun(latest.run_id);
        if (!alive.current) return;
        setRun(data);
        if (data && data.run.status === 'running') poll(data.run.run_id);
      } else {
        setRun(null);
      }
    } catch { /* leave the panel dormant on error */ }
  }, [auth, courseId, loadRun, poll]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Default every suggestion to "keep" whenever a set opens for review.
  React.useEffect(() => {
    if (run && run.run.status === 'pending_review') {
      setSelNew(new Set(run.newTeeboxes.map((t) => t.course_teebox_id as number)));
      setSelUpd(new Set(run.proposedUpdates.map((u) => u.update_id)));
      setExpandedTee(new Set());
    }
  }, [run]);

  const start = async () => {
    setBusy(true); setError(null); setDecline(null);
    try {
      const cfg = await auth();
      const res = await axios.post(`${API_BASE_URL}/courses/${courseId}/teebox-agent`, {}, cfg);
      const runId = res.data.run_id;
      const data = await loadRun(runId);
      if (!alive.current) return;
      setRun(data);
      if (data && data.run.status === 'running') poll(runId);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to start the agent.');
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const ALLOWED_UPLOAD = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-selected later
    if (!file) return;
    if (!ALLOWED_UPLOAD.includes(file.type)) { setError('Unsupported file type. Upload a PDF, JPG, PNG, GIF, or WebP.'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('File is too large (max 20 MB).'); return; }
    setBusy(true); setError(null); setDecline(null);
    try {
      const cfg = await auth();
      const res = await axios.post(
        `${API_BASE_URL}/courses/${courseId}/teebox-scorecard?filename=${encodeURIComponent(file.name)}`,
        file,
        { headers: { ...cfg.headers, 'Content-Type': file.type || 'application/octet-stream' } },
      );
      const runId = res.data.run_id;
      const data = await loadRun(runId);
      if (!alive.current) return;
      setRun(data);
      if (data && data.run.status === 'running') poll(runId);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to read the scorecard.');
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const stopRun = async () => {
    if (!run) return;
    pollToken.current++; // cancel the active poll immediately
    setBusy(true);
    try {
      const cfg = await auth();
      await axios.post(`${API_BASE_URL}/teebox-agent-runs/${run.run.run_id}/stop`, {}, cfg);
      if (!alive.current) return;
      setRun(null);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to stop the search.');
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const act = async (verb: 'confirm' | 'decline' | 'retry') => {
    if (!run) return;
    if (verb === 'decline' && !window.confirm('Decline this set? Its AI-added tee boxes are removed (any with rounds are kept), and proposed updates are discarded.')) return;
    setBusy(true); setError(null); setDecline(null);
    try {
      const cfg = await auth();
      const body = verb === 'confirm' ? { teebox_ids: Array.from(selNew), update_ids: Array.from(selUpd) } : {};
      const res = await axios.post(`${API_BASE_URL}/teebox-agent-runs/${run.run.run_id}/${verb}`, body, cfg);
      await onChanged(); // reflect new/updated tee boxes in the course list
      if (!alive.current) return;
      if (verb === 'confirm') { setRun(null); if ((res.data.keptWithRounds || []).length) setDecline({ deleted: [], kept: res.data.keptWithRounds }); }
      else if (verb === 'decline') { setDecline({ deleted: res.data.deleted || [], kept: res.data.kept || [] }); setRun(null); }
      else if (verb === 'retry') { const data = await loadRun(res.data.newRunId); setRun(data); if (data?.run.status === 'running') poll(res.data.newRunId); }
    } catch (e: any) {
      setError(e?.response?.data?.error || `Failed to ${verb} the set.`);
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const status = run?.run.status;
  const searching = status === 'running';
  const reviewing = status === 'pending_review';

  return (
    <div className="cx-agent">
      {!run && (
        <div className="cx-agent-triggers">
          <button className="cx-btn cx-btn-ghost cx-btn-sm cx-agent-trigger" disabled={busy} onClick={start}>
            {busy ? 'Working…' : '🔎 Find tee boxes (AI)'}
          </button>
          <button className="cx-btn cx-btn-ghost cx-btn-sm cx-agent-trigger" disabled={busy} onClick={() => fileRef.current?.click()}>
            📄 Upload scorecard
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/gif,image/webp"
            style={{ display: 'none' }}
            onChange={onFile}
          />
        </div>
      )}
      {error && <div className="cx-agent-err">{error}</div>}

      {searching && (
        <div className="cx-agent-searching">
          <span className="cx-spin" />
          <span>{run?.run.trigger === 'upload' ? 'Reading the uploaded scorecard…' : 'Searching the web and cross-checking sources for tee boxes…'}</span>
          <button type="button" className="cx-btn cx-btn-danger cx-btn-sm cx-agent-stop" disabled={busy} onClick={stopRun}>
            {busy ? 'Stopping…' : 'Stop'}
          </button>
        </div>
      )}

      {status === 'failed' && (
        <div className="cx-agent-card">
          <div className="cx-agent-err">{run!.run.error || 'The agent could not identify this course.'}</div>
          <div className="cx-agent-btns">
            <button className="cx-btn cx-btn-secondary cx-btn-sm" disabled={busy} onClick={() => act('retry')}>Retry</button>
            <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => act('decline')}>Dismiss</button>
          </div>
        </div>
      )}

      {reviewing && run && (
        <div className="cx-agent-card">
          <div className="cx-agent-head">
            <span className="cx-badge cx-badge-blue">AI • review the set</span>
            <span className="cx-agent-note">These tee boxes are already usable. Confirm to keep them, or decline to remove them.</span>
          </div>
          {run.run.summary && <div className="cx-agent-summary">{run.run.summary}</div>}

          {run.newTeeboxes.length > 0 && (() => {
            const ids = run.newTeeboxes.map((t) => t.course_teebox_id as number);
            const allSel = ids.length > 0 && ids.every((id) => selNew.has(id));
            const toggleAll = () => setSelNew(allSel ? new Set() : new Set(ids));
            const toggleOne = (id: number) => { const n = new Set(selNew); n.has(id) ? n.delete(id) : n.add(id); setSelNew(n); };
            const toggleHoles = (id: number) => { const n = new Set(expandedTee); n.has(id) ? n.delete(id) : n.add(id); setExpandedTee(n); };
            return (
              <div className="cx-agent-sect">
                <div className="cx-agent-sect-title">Suggested tee boxes ({run.newTeeboxes.length}) — pick which to add</div>
                <FitTable className="cx-agent-tablewrap">
                  <table className="tbl cx-agent-table">
                    <thead>
                      <tr>
                        <th className="cx-agent-th-chk"><input type="checkbox" checked={allSel} onChange={toggleAll} title="Select all" /></th>
                        <th>Tee box</th>
                        <th className="cx-num">Rating</th>
                        <th className="cx-num">Slope</th>
                        <th className="cx-num">Par</th>
                        <th className="cx-num">Yards</th>
                        <th className="cx-num">Holes</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {run.newTeeboxes.map((t) => {
                        const id = t.course_teebox_id as number;
                        const holes = Array.from({ length: 18 }).filter((_, i) => t[`hole_${i + 1}_par`] != null || t[`hole_${i + 1}_yards`] != null).length;
                        const sel = selNew.has(id);
                        const open = expandedTee.has(id);
                        return (
                          <React.Fragment key={id}>
                            <tr className={sel ? '' : 'cx-agent-tr-off'}>
                              <td><input type="checkbox" checked={sel} onChange={() => toggleOne(id)} /></td>
                              <td><b>{t.course_teebox_name}</b></td>
                              <td className="cx-num">{fmtVal(t.course_teebox_rating)}</td>
                              <td className="cx-num">{fmtVal(t.course_teebox_slope)}</td>
                              <td className="cx-num">{fmtVal(t.course_teebox_par)}</td>
                              <td className="cx-num">{fmtVal(t.course_teebox_yards)}</td>
                              <td className="cx-num">{holes ? `${holes}/18` : '—'}</td>
                              <td>{holes > 0 && (
                                <button type="button" className="cx-agent-expand" onClick={() => toggleHoles(id)}>{open ? 'Hide' : 'Holes'}</button>
                              )}</td>
                            </tr>
                            {open && holes > 0 && (
                              <tr className="cx-agent-holerow">
                                <td colSpan={8}>
                                  <FitTable className="cx-agent-holewrap">
                                    <table className="tbl cx-agent-holetable">
                                      <thead><tr><th>Hole</th>{Array.from({ length: 18 }).map((_, i) => <th key={i}>{i + 1}</th>)}</tr></thead>
                                      <tbody>
                                        <tr><td>Par</td>{Array.from({ length: 18 }).map((_, i) => <td key={i}>{fmtVal(t[`hole_${i + 1}_par`])}</td>)}</tr>
                                        <tr><td>Yds</td>{Array.from({ length: 18 }).map((_, i) => <td key={i}>{fmtVal(t[`hole_${i + 1}_yards`])}</td>)}</tr>
                                      </tbody>
                                    </table>
                                  </FitTable>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </FitTable>
              </div>
            );
          })()}

          {run.proposedUpdates.length > 0 && (
            <div className="cx-agent-sect">
              <div className="cx-agent-sect-title">Proposed updates to existing tee boxes ({run.proposedUpdates.length}) — pick which to apply</div>
              {run.proposedUpdates.map((u) => {
                const sel = selUpd.has(u.update_id);
                const toggleUpd = () => { const n = new Set(selUpd); n.has(u.update_id) ? n.delete(u.update_id) : n.add(u.update_id); setSelUpd(n); };
                return (
                  <div key={u.update_id} className={`cx-agent-upd ${sel ? '' : 'cx-agent-tr-off'}`}>
                    <label className="cx-agent-upd-head">
                      <input type="checkbox" checked={sel} onChange={toggleUpd} />
                      <b>{u.target_name}</b>
                    </label>
                    {u.changes.length === 0
                      ? <span className="cx-agent-holes"> — no differences found</span>
                      : (
                        <div className="cx-agent-diffs">
                          {u.changes.map((c) => (
                            <span key={c.field} className="cx-agent-diff">
                              {fieldLabel(c.field)}: <s>{fmtVal(c.from)}</s> → <b>{fmtVal(c.to)}</b>
                            </span>
                          ))}
                        </div>
                      )}
                  </div>
                );
              })}
            </div>
          )}

          {run.newTeeboxes.length === 0 && run.proposedUpdates.length === 0 && (
            <div className="cx-agent-holes">The agent didn't find any tee boxes to add or update.</div>
          )}

          {(run.run.sources?.length ?? 0) > 0 && (
            <div className="cx-agent-sources">
              Sources:{' '}
              {run.run.sources!.slice(0, 6).map((s, i) => (
                <a key={i} href={s} target="_blank" rel="noreferrer">[{i + 1}]</a>
              ))}
            </div>
          )}

          <div className="cx-agent-btns">
            <button className="cx-btn cx-btn-primary cx-btn-sm" disabled={busy || (selNew.size === 0 && selUpd.size === 0)} onClick={() => act('confirm')}>
              Confirm {selNew.size + selUpd.size} selected
            </button>
            <button className="cx-btn cx-btn-danger cx-btn-sm" disabled={busy} onClick={() => act('decline')}>Decline the set</button>
            <button className="cx-btn cx-btn-ghost cx-btn-sm" disabled={busy} onClick={() => act('retry')}>Retry</button>
          </div>
        </div>
      )}

      {decline && (
        <div className="cx-agent-card">
          {decline.deleted.length > 0 && <div className="cx-agent-holes">Removed {decline.deleted.length} tee box{decline.deleted.length === 1 ? '' : 'es'}.</div>}
          {decline.kept.map((k) => (
            <div key={k.course_teebox_id} className="cx-agent-kept">
              <div className="cx-agent-kept-msg">⚠️ {k.message} Kept as pending.</div>
              <ul className="cx-agent-kept-rounds">
                {k.rounds.map((r: any) => (
                  <li key={r.round_id}>
                    {r.round_date ? String(r.round_date).slice(0, 10) : 'round'} · {r.course_name || 'course'}{r.number_of_holes ? ` · ${r.number_of_holes} holes` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="cx-agent-btns">
            <button className="cx-btn cx-btn-ghost cx-btn-sm" onClick={() => setDecline(null)}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Styles ──────────────────────────────────────────────────────────────── */

const cx = `
.cx-page{background:#f8fafc;min-height:100vh;padding:24px 16px;font-family:'Inter',-apple-system,BlinkMacSystemFont,sans-serif;color:#1e293b}
.cx-inner{width:100%;max-width:1040px;margin:0 auto}
.cx-gate{background:#fff;border:1px solid #e2e8f0;border-radius:14px;padding:40px 24px;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,.06)}
.cx-header{display:flex;align-items:flex-end;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:16px}
.cx-h1{font-size:26px;font-weight:800;margin:0;letter-spacing:-0.01em;color:#0f172a}
.cx-sub{color:#64748b;font-size:13.5px;margin:5px 0 0}

.cx-toolbar{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;margin-bottom:18px}
.cx-search{position:relative;display:flex;align-items:center;flex:1 1 280px;max-width:440px}
.cx-search-ico{position:absolute;left:11px;font-size:13px;opacity:.6;pointer-events:none}
.cx-search-input{width:100%;border:1px solid #e2e8f0;border-radius:10px;padding:9px 32px 9px 32px;font-size:14px;background:#fff;outline:none}
.cx-search-input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.12)}
.cx-search-clear{position:absolute;right:8px;border:none;background:#e2e8f0;color:#475569;width:20px;height:20px;border-radius:50%;cursor:pointer;font-size:11px;line-height:1;display:flex;align-items:center;justify-content:center}
.cx-stats{display:flex;align-items:center;gap:8px;font-size:13px;color:#64748b;white-space:nowrap}
.cx-stat b{color:#0f172a;font-weight:800}
.cx-stat-dot{color:#cbd5e1}
.cx-stat-warn b{color:#b45309}

.cx-loading{padding:48px;text-align:center;color:#94a3b8}
.cx-empty{background:#fff;border:1px dashed #cbd5e1;border-radius:12px;padding:36px 20px;text-align:center;color:#94a3b8;font-size:14px}
.cx-empty-sm{color:#94a3b8;font-size:13px;padding:6px 2px}
.cx-note{color:#64748b;font-size:12.5px;background:#f8fafc;border:1px solid #eef2f7;border-radius:8px;padding:8px 12px}

/* Facility card */
.cx-fac{background:#fff;border:1px solid #e2e8f0;border-radius:14px;margin-bottom:14px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,.05)}
.cx-fac-head{display:flex;align-items:center;gap:12px;padding:14px 16px;cursor:pointer;transition:background .12s}
.cx-fac-head:hover{background:#fbfdff}
.cx-fac-id{flex:1;min-width:0;display:flex;flex-direction:column;gap:5px}
.cx-fac-title-static{font-size:17px;font-weight:700;color:#0f172a}
.cx-fac-meta{display:flex;align-items:center;gap:6px;flex-wrap:wrap;justify-content:flex-end}
.cx-fac-body{padding:4px 16px 16px}

/* Chevron */
.cx-chev{display:inline-flex;align-items:center;justify-content:center;color:#94a3b8;font-size:13px;border:none;background:none;cursor:pointer;transition:transform .15s;padding:0;width:18px;flex:0 0 18px}
.cx-chev.open{transform:rotate(90deg)}
.cx-chev-spacer{width:18px;flex:0 0 18px}

/* Editable title input */
.cx-title-input{font-size:17px;font-weight:700;color:#0f172a;width:100%;border:1px solid transparent;border-radius:8px;padding:5px 9px;background:transparent;outline:none;transition:border-color .12s,background .12s}
.cx-title-input:hover{border-color:#e2e8f0}
.cx-title-input:focus{border-color:#2563eb;background:#fff;box-shadow:0 0 0 3px rgba(37,99,235,.1)}
.cx-title-sm{font-size:15px;font-weight:600;flex:1 1 180px;min-width:140px}
.cx-addr-input{font-size:12.5px;color:#64748b;width:100%;border:1px solid transparent;border-radius:8px;padding:4px 9px;background:transparent;outline:none}
.cx-addr-input:hover{border-color:#e2e8f0}
.cx-addr-input:focus{border-color:#2563eb;background:#fff}

/* Badges */
.cx-badge{display:inline-block;font-size:11px;font-weight:700;border-radius:999px;padding:3px 9px;white-space:nowrap;letter-spacing:.01em}
.cx-badge-blue{background:#dbeafe;color:#1d4ed8}
.cx-badge-amber{background:#fef3c7;color:#b45309}
.cx-badge-gray{background:#f1f5f9;color:#64748b}
.cx-badge-green{background:#dcfce7;color:#15803d}

/* Action rows */
.cx-actions{display:flex;gap:8px;flex-wrap:wrap;align-items:center;padding:12px 16px;border-top:1px solid #f1f5f9}
.cx-push{margin-left:auto}

/* Evident expand/collapse bars */
.cx-expand{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;border:none;border-top:1px solid #e2e8f0;background:#f1f5f9;color:#1d4ed8;font-weight:800;font-size:13px;padding:11px 16px;cursor:pointer;transition:background .12s}
.cx-expand:hover{background:#e6edf7}
.cx-expand .cx-chev{color:#1d4ed8;width:auto;flex:0 0 auto}
.cx-expand-sub{background:#f3f7fc;font-size:12.5px;font-weight:700;padding:9px 12px}
.cx-expand-sub:hover{background:#e9f1fb}

/* Buttons */
.cx-btn{border:1px solid transparent;border-radius:9px;padding:9px 16px;font-size:13.5px;font-weight:700;cursor:pointer;transition:background .12s,border-color .12s,opacity .12s;white-space:nowrap;line-height:1.1}
.cx-btn:disabled{opacity:.6;cursor:default}
.cx-btn-sm{padding:6px 12px;font-size:12.5px;border-radius:8px}
.cx-btn-primary{background:#2563eb;color:#fff}
.cx-btn-primary:hover:not(:disabled){background:#1d4ed8}
.cx-btn-secondary{background:#fff;color:#334155;border-color:#e2e8f0}
.cx-btn-secondary:hover{background:#f8fafc;border-color:#cbd5e1}
.cx-btn-ghost{background:transparent;color:#64748b;border-color:transparent}
.cx-btn-ghost:hover{background:#f1f5f9;color:#334155}
.cx-btn-danger{background:#fff;color:#dc2626;border-color:#fecaca}
.cx-btn-danger:hover{background:#fef2f2;border-color:#fca5a5}

/* Courses */
.cx-courses{display:flex;flex-direction:column;gap:10px;margin-top:8px}
.cx-course{border:1px solid #e8edf3;border-radius:12px;background:#fcfdff;overflow:hidden}
.cx-course.inactive{background:#f8fafc;border-style:dashed;opacity:.9}
.cx-course-head{display:flex;align-items:center;gap:10px;padding:10px 12px;flex-wrap:wrap}
.cx-course-tags{display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.cx-course-actions{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-left:auto}

/* Tee boxes */
.cx-tees{padding:4px 12px 14px;border-top:1px solid #eef2f7}
.cx-tees-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin:10px 0}
.cx-tees-title{font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#94a3b8}
.cx-tee{border:1px solid #e8edf3;border-radius:10px;padding:12px;margin-bottom:10px;background:#fff}
.cx-tee-fields{display:grid;grid-template-columns:repeat(auto-fit,minmax(108px,1fr));gap:10px}
.cx-field{display:flex;flex-direction:column;gap:4px;min-width:0}
.cx-field-wide{grid-column:span 2;min-width:160px}
.cx-label{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#94a3b8}
.cx-input{width:100%;border:1px solid #e2e8f0;border-radius:8px;padding:8px 10px;font-size:13.5px;background:#fff;outline:none;box-sizing:border-box}
.cx-input:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.1)}

.cx-holes-toggle{display:inline-flex;align-items:center;gap:7px;margin-top:12px;background:#f8fafc;border:1px solid #eef2f7;border-radius:8px;padding:7px 12px;font-size:13px;font-weight:700;color:#334155;cursor:pointer}
.cx-holes-toggle:hover{background:#f1f5f9}
.cx-muted{color:#94a3b8;font-weight:600}

.cx-nines{display:flex;flex-direction:column;gap:12px;margin-top:12px}
.cx-nine-head{display:flex;align-items:center;justify-content:space-between;font-size:12px;font-weight:800;color:#475569;margin-bottom:7px}
.cx-nine-par{color:#2563eb}
.cx-holes{display:grid;grid-template-columns:repeat(auto-fill,minmax(62px,1fr));gap:7px}
.cx-hole{display:flex;flex-direction:column;gap:3px;text-align:center;background:#f8fafc;border:1px solid #eef2f7;border-radius:8px;padding:6px 4px}
.cx-hole-n{font-size:11px;font-weight:800;color:#64748b}
.cx-hole-par{width:100%;font-size:12px;padding:3px 2px;border-radius:6px;border:1px solid #e2e8f0;background:#fff;outline:none}
.cx-hole-yds{width:100%;font-size:12px;padding:3px 4px;border-radius:6px;border:1px solid #e2e8f0;background:#fff;outline:none;box-sizing:border-box}
.cx-hole-par:focus,.cx-hole-yds:focus{border-color:#2563eb}

.cx-tee-actions{display:flex;gap:8px;margin-top:12px}

/* Aliases */
.cx-aliases{display:flex;align-items:center;flex-wrap:wrap;gap:6px;padding:0 16px 10px}
.cx-aliases-course{padding:0 12px 10px}
.cx-aliases-label{font-size:10.5px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#94a3b8;margin-right:2px}
.cx-alias{display:inline-flex;align-items:center;gap:5px;background:#eef2ff;color:#4338ca;border:1px solid #e0e7ff;border-radius:999px;padding:3px 8px;font-size:12px;font-weight:600}
.cx-alias-x{border:none;background:none;color:#818cf8;cursor:pointer;font-size:10px;line-height:1;padding:0}
.cx-alias-x:hover{color:#4338ca}

/* Solid danger button (used for irreversible confirm) */
.cx-btn-danger-solid{background:#dc2626;color:#fff;border-color:#dc2626}
.cx-btn-danger-solid:hover:not(:disabled){background:#b91c1c;border-color:#b91c1c}

/* Merge modal */
.cx-modal-overlay{position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;padding:16px;z-index:1000}
.cx-modal{background:#fff;border-radius:16px;width:100%;max-width:520px;max-height:90vh;overflow-y:auto;box-shadow:0 20px 50px rgba(0,0,0,.25)}
.cx-modal-head{display:flex;align-items:center;justify-content:space-between;padding:18px 20px;border-bottom:1px solid #f1f5f9}
.cx-modal-title{font-size:18px;font-weight:800;margin:0;color:#0f172a}
.cx-modal-x{border:none;background:#f1f5f9;color:#475569;width:28px;height:28px;border-radius:8px;cursor:pointer;font-size:13px}
.cx-modal-x:hover{background:#e2e8f0}
.cx-modal-body{padding:18px 20px;display:flex;flex-direction:column;gap:12px}
.cx-modal-lead{font-size:14px;color:#334155;margin:0;line-height:1.5}
.cx-modal-label{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#94a3b8;margin-bottom:-6px}
.cx-modal-select{width:100%;border:1px solid #e2e8f0;border-radius:10px;padding:10px 12px;font-size:14px;background:#fff;outline:none}
.cx-modal-select:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.12)}
.cx-modal-note{font-size:12.5px;color:#94a3b8}
.cx-modal-what{background:#f8fafc;border:1px solid #eef2f7;border-radius:10px;padding:10px 14px}
.cx-modal-what-title{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#64748b;margin-bottom:6px}
.cx-modal-what ul{margin:0;padding-left:18px;display:flex;flex-direction:column;gap:5px}
.cx-modal-what li{font-size:13px;color:#475569;line-height:1.45}
.cx-modal-warn{background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:10px 14px;font-size:13px;color:#b91c1c;line-height:1.45}
.cx-modal-check{display:flex;align-items:flex-start;gap:9px;font-size:13.5px;color:#334155;cursor:pointer;line-height:1.4}
.cx-modal-check input{margin-top:2px;width:16px;height:16px;cursor:pointer;flex:0 0 16px}
.cx-modal-actions{display:flex;justify-content:flex-end;gap:10px;padding:16px 20px;border-top:1px solid #f1f5f9}

/* Tee box merge comparison */
.cx-cmp{display:flex;flex-direction:column;gap:10px}
.cx-cmp-rounds{font-size:13px;color:#334155;background:#eff6ff;border:1px solid #dbeafe;border-radius:10px;padding:10px 14px;line-height:1.45}
.cx-cmp-table{--fit-base:12.5px}
.cx-cmp-table th{background:transparent;font-weight:800;letter-spacing:.03em;color:#94a3b8;padding:.5em .65em;border-bottom:1px solid #e2e8f0}
.cx-cmp-table td{padding:.55em .65em;border-bottom:1px solid #f1f5f9;color:#334155}
.cx-cmp-table td:first-child{font-weight:700;color:#0f172a}
.cx-cmp-table td:last-child{font-weight:700;white-space:nowrap}
.cx-cmp-match td:last-child{color:#15803d}
.cx-cmp-differ{background:#fff7ed}
.cx-cmp-differ td:last-child{color:#c2410c}
.cx-cmp-target-missing{background:#fef2f2}
.cx-cmp-target-missing td:last-child{color:#b91c1c}
.cx-cmp-source-missing td:last-child,.cx-cmp-both-missing td:last-child{color:#94a3b8}
.cx-cmp-warn-title{font-weight:800;margin-bottom:6px}
.cx-cmp-warn-list{margin:0;padding-left:18px;display:flex;flex-direction:column;gap:5px}
.cx-cmp-warn-list li{line-height:1.45}
.cx-cmp-ok{font-size:13px;color:#15803d;background:#f0fdf4;border:1px solid #dcfce7;border-radius:10px;padding:10px 14px}

/* Tee-box agent panel */
.cx-agent{margin:8px 0 4px}
.cx-agent-triggers{display:flex;gap:8px;flex-wrap:wrap}
.cx-agent-trigger{color:#4338ca;border-color:#c7d2fe;background:#eef2ff}
.cx-agent-err{font-size:12.5px;color:#b91c1c;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:8px 12px;margin-top:8px}
.cx-agent-searching{display:flex;align-items:center;gap:9px;font-size:13px;color:#475569;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:10px 14px;margin-top:8px}
.cx-agent-stop{margin-left:auto;flex:0 0 auto}
.cx-spin{width:13px;height:13px;border-radius:50%;border:2px solid #c7d2fe;border-top-color:#4338ca;display:inline-block;animation:cxspin .8s linear infinite;flex:0 0 auto}
@keyframes cxspin{to{transform:rotate(360deg)}}
.cx-agent-card{border:1px solid #e2e8f0;border-radius:12px;background:#fff;padding:12px 14px;margin-top:8px;box-shadow:0 1px 2px rgba(0,0,0,.04)}
.cx-agent-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:8px}
.cx-agent-note{font-size:12px;color:#64748b}
.cx-agent-summary{font-size:12.5px;color:#475569;background:#f8fafc;border:1px solid #eef2f7;border-radius:8px;padding:8px 11px;margin-bottom:10px;line-height:1.45}
.cx-agent-sect{margin:10px 0}
.cx-agent-sect-title{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:#94a3b8;margin-bottom:6px}
.cx-agent-row{display:flex;gap:12px;flex-wrap:wrap;align-items:baseline;font-size:12.5px;color:#334155;padding:6px 2px;border-bottom:1px solid #f5f7fa}
.cx-agent-row b{color:#0f172a;min-width:96px}
.cx-agent-holes{color:#94a3b8;font-size:12px}
.cx-agent-upd{font-size:12.5px;color:#334155;padding:6px 2px;border-bottom:1px solid #f5f7fa}
.cx-agent-upd b{color:#0f172a}
.cx-agent-diffs{display:flex;flex-wrap:wrap;gap:6px 14px;margin-top:4px}
.cx-agent-diff{font-size:12px;color:#475569}
.cx-agent-diff s{color:#b91c1c}
.cx-agent-diff b{color:#15803d}
.cx-agent-sources{font-size:12px;color:#64748b;margin-top:8px;display:flex;gap:6px;flex-wrap:wrap}
.cx-agent-sources a{color:#2563eb;text-decoration:none}
.cx-agent-btns{display:flex;gap:8px;flex-wrap:wrap;margin-top:12px}
.cx-agent-kept{margin-top:8px}
.cx-agent-kept-msg{font-size:12.5px;color:#b45309;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:8px 11px}
.cx-agent-kept-rounds{margin:6px 0 0;padding-left:20px;font-size:12px;color:#64748b;display:flex;flex-direction:column;gap:3px}
.cx-num{text-align:right;font-variant-numeric:tabular-nums}
.cx-agent-tablewrap{border:1px solid #eef2f7;border-radius:10px}
.cx-agent-table{--fit-base:12.5px}
.cx-agent-table th{background:#f8fafc;color:#64748b;letter-spacing:.04em;border-bottom:1px solid #e2e8f0}
.cx-agent-table td{border-bottom:1px solid #f5f7fa;color:#334155}
.cx-agent-table tr:last-child td{border-bottom:none}
.cx-agent-table td b{color:#0f172a}
.cx-agent-th-chk{width:1%;white-space:nowrap;text-align:center}
.cx-agent-table th.cx-num,.cx-agent-table td.cx-num{text-align:right}
.cx-agent-tr-off{opacity:.45}
.cx-agent-expand{border:none;background:#eef2ff;color:#4338ca;border-radius:6px;padding:.2em .7em;font-size:.88em;font-weight:700;cursor:pointer}
.cx-agent-holerow td{background:#f8fafc;padding:8px 10px}
.cx-agent-holewrap{width:0;min-width:100%}
.cx-agent-holetable{--fit-base:11px}
.cx-agent-holetable th{font-size:1em;letter-spacing:0;text-transform:none;background:transparent}
.cx-agent-holetable th,.cx-agent-holetable td{border:1px solid #e8edf3;padding:.27em .55em;text-align:center;color:#475569;white-space:nowrap}
.cx-agent-holetable td:first-child,.cx-agent-holetable th:first-child{text-align:left;font-weight:700;color:#334155;background:#fff;position:sticky;left:0}
.cx-agent-upd-head{display:flex;align-items:center;gap:8px;cursor:pointer;margin-bottom:2px}

@media (max-width:640px){
  .cx-page{padding:16px 12px}
  .cx-h1{font-size:22px}
  .cx-fac-head{align-items:flex-start}
  .cx-fac-meta{flex-direction:column;align-items:flex-end;gap:4px}
  .cx-course-actions{margin-left:0;width:100%}
  .cx-course-actions .cx-btn{flex:1 1 auto}
  .cx-field-wide{grid-column:span 2}
  .cx-actions .cx-push{margin-left:0}
}
`;

export default Courses;
